import React, { useState } from 'react';
import { DealerStaticViewer } from '@wix/react-dealer-viewer';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import CloseIconBold from '@wix/da-ds/pkg/Icons/CloseBold';
import ButtonB5 from '@wix/da-shared-react/pkg/Button/ButtonB5';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useDealerBanners, sendDealerCommand } from './useDealerBanners';

import s from './DealerAd.scss';

export interface Props {
  realEstateId: string;
  offerName: string;
  theme: string;
  width?: number;
  height?: number;
  userGuid: string;
  biClientId: string;
  biClientIdSigned: string;
  additionalParams?: { [s: string]: string }; // TODO remove if we dont use it
  withCloseButton: boolean;
  translationVersion: string;
}

const DealerAd: React.FC<Props> = ({
  realEstateId,
  offerName = '',
  width = 0,
  height = 0,
  userGuid = '',
  additionalParams,
  biClientId,
  withCloseButton,
  translationVersion,
  biClientIdSigned,
}) => {
  const {
    isEmpty,
    isLoaded,
    offers: dealerOffers,
    onEmpty,
    closeOffer,
  } = useDealerBanners(realEstateId, biClientIdSigned, userGuid);
  const [newBannerHovered, setNewBannerHovered] = useState(false);
  const [newModalSent, setNewModalSent] = useState(false);

  if (isEmpty || (isLoaded && !dealerOffers.length)) {
    return null; // collapse
  }

  const offers = offerName
    ? dealerOffers.filter(offer => offer.offerName === offerName)
    : dealerOffers;

  if (!offers.length) {
    return null;
  }

  // New-world offers with 'creative' content instead of 'payloadJson'
  // These we have to render/handle ourselves
  if (isLoaded && offers[0]?.asset?.['creative']) {
    for (const offer of offers) {
      if (typeof offer.asset['creative']['oneOfSelection'] !== 'undefined') {
        const creative = offer.asset['creative']['oneOfSelection'];

        // Modal: Send up to the top window
        if (typeof creative['modal'] !== 'undefined') {
          if (!newModalSent) {
            setNewModalSent(true);
            sendDealerCommand('show-modal', realEstateId, creative['modal']);
          }
        }

        // Banner: Render the image(s)
        // TODO: There may be a way to use react-dealer-viewer's components here
        if (typeof creative['banner'] !== 'undefined') {
          if (
            (width && creative['banner']['bannerImageWidth'] !== width) ||
            (height && creative['banner']['bannerImageHeight'] !== height)
          ) {
            // Width/height were given, but this banner doesn't match
            continue;
          } else {
            return (
              <a
                href={creative['banner']['bannerURL']}
                onMouseOver={() => {
                  setNewBannerHovered(true);
                }}
                onMouseOut={() => {
                  setNewBannerHovered(false);
                }}
              >
                {newBannerHovered && creative['banner']['bannerImageHover'] ? (
                  <img
                    src={creative['banner']['bannerImageHover']}
                    alt="Banner"
                  />
                ) : (
                  <img src={creative['banner']['bannerImage']} alt="Banner" />
                )}
              </a>
            );
          }
        }
      }
    }
    return null;
  }

  // Old-world offers with a 'payloadJson', pass to react-dealer-viewer
  const offer = offers[0];
  return (
    <ErrorBoundary debugComponent="DealerAd">
      <div className={s['root']}>
        {isLoaded && (
          <div key={offer.offerGuid} className={s['banner-wrapper']}>
            <DealerStaticViewer
              dealerOptions={additionalParams}
              bannerPosition={realEstateId}
              locale="en"
              metasiteGuid=""
              translationsUrl={`https://static.parastorage.com/services/dealer-cms-translations/${translationVersion}/`}
              offer={offer}
              additionalBiParams={{
                _uuid: userGuid || '',
                uuid: userGuid || '',
                client_id: biClientId || '',
              }}
              onEmpty={onEmpty}
              onError={onEmpty}
            />

            {withCloseButton && (
              <ButtonB5
                className={s['close-button']}
                icon={CloseIconBold}
                iconProps={{ size: IconSize.MEDIUM }}
                onClick={() => closeOffer(offer)}
                biData={{
                  evid: BiEventType.CLOSE_BANNER_AD,
                  realestateid: realEstateId,
                  campaignid: offer.offerGuid,
                }}
              />
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};
DealerAd.displayName = 'DealerAd';

// we just don't care for prop changes, period
export default React.memo(DealerAd, () => true);
